import { Either, Stream } from 'effect'

import { AgentApiError } from './agentApi'

// SSE（text/event-stream）の読み取り。EventSource は POST とヘッダーを送れないので、fetch の応答の本文を読む。playground の hooks/sse.ts から移した

export interface SseEvent {
  event: string
  data: string
}

// 空行で区切られた1つのかたまりを、イベントにする。data のないかたまり（: keep-alive などのコメント行だけのもの）は捨てる
const parseSseBlock = (block: string): SseEvent | null => {
  const lines = block.split('\n')
  const eventLine = lines.findLast((line) => line.startsWith('event:'))
  const dataLines = lines
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).replace(/^ /v, ''))
  return dataLines.length === 0
    ? null
    : { event: eventLine?.slice('event:'.length).trim() ?? 'message', data: dataLines.join('\n') }
}

/**
 * 受け取った文字列から、区切りまで届いたイベントを取り出す。最後の区切りより後ろは、次に届く文字列とつないで読むので rest に返す。
 */
export const splitSseEvents = (received: string): { events: readonly SseEvent[]; rest: string } => {
  const blocks = received.replaceAll('\r\n', '\n').split('\n\n')
  return {
    events: blocks.slice(0, -1).flatMap((block) => {
      const event = parseSseBlock(block)
      return event === null ? [] : [event]
    }),
    rest: blocks.at(-1) ?? '',
  }
}

/**
 * SSE の data を JSON として読む。JSON でなければ文字列のまま返す（スキーマの検証で落とす）。
 */
export const parseSseData = (data: string): unknown =>
  Either.getOrElse(
    Either.try((): unknown => JSON.parse(data)),
    () => data,
  )

/**
 * fetch の応答の本文を SSE として読み、イベントを順に流す。本文が途中で切れたら（BE のデプロイ、ネットワークの切断など）失敗で終わる。
 */
export const readSseEvents = (response: Response): Stream.Stream<SseEvent, AgentApiError> => {
  const { body } = response
  if (body === null) {
    return Stream.empty
  }
  return Stream.fromReadableStream({
    evaluate: () => body.pipeThrough(new TextDecoderStream()),
    onError: (cause) => new AgentApiError({ reason: 'streamBroken', status: response.status, body: null, cause }),
  }).pipe(
    Stream.mapAccum('', (rest, chunk) => {
      const split = splitSseEvents(rest + chunk)
      return [split.rest, split.events]
    }),
    Stream.flattenIterables,
  )
}
